import { Component, OnDestroy, OnInit } from '@angular/core';
import { LocalStoreService } from '../../../../../common/services/storage.service';
import { DecimalPipe } from '@angular/common';
import { SwalService } from '../../../../../common/services/swal.service';
import PaymentRequest from '../../../../../core/data/dtos/payment.request';
import { PaymentService } from '../../../services/payment.service';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SharedModule } from '../../../../../common/shared.module';
import { policyMask } from '../../../../../common/constants/masks';
import { NavigationEnd, Router } from '@angular/router';
import { NgxMaskDirective, NgxMaskPipe } from 'ngx-mask';
import { lastValueFrom } from 'rxjs';
import { Subscription } from 'rxjs/internal/Subscription';

@Component({
  selector: 'app-checkout',
  providers: [LocalStoreService],
  imports: [
    DecimalPipe,
    FormsModule,
    ReactiveFormsModule,
    NgxMaskDirective,
    NgxMaskPipe,
    SharedModule,
  ],
  templateUrl: './checkout.component.html',
  styleUrl: './checkout.component.css',
})
export class CheckoutComponent implements OnInit, OnDestroy {
  paymentRequest: PaymentRequest = {} as PaymentRequest;

  routerSubscription?: Subscription;

  paymentForm = this.fb.group({
    creditCard: ['', Validators.required],
    expirationMonth: ['', Validators.required],
    expirationYear: ['', Validators.required],
    cvv: ['', Validators.required],
  });
  policyMask = policyMask;

  constructor(
    private swalService: SwalService,
    private fb: FormBuilder,
    private paymentService: PaymentService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // Logic to re-fetch data or reload content
        this.loadPolicies();
      }
    });

    this.loadPolicies();
  }

  ngOnDestroy() {
    this.routerSubscription?.unsubscribe();
  }

  loadPolicies() {
    const policies = this.paymentService.getStoredPolicies();
    if (policies) {
      this.paymentRequest = policies;
    } else {
      this.paymentRequest = {} as PaymentRequest;
      this.swalService.notification(
        'Debe indicar al menos una póliza para realizar el pago',
        true,
      );
    }
  }

  getCardProvider(pan: string) {
    const cleaned = pan.replace(/\D/g, '');

    if (/^4[0-9]{12}(?:[0-9]{3})?$/.test(cleaned)) {
      return 'Visa';
    } else if (
      /^5[1-5][0-9]{14}$/.test(cleaned) ||
      /^2(2[2-9][0-9]{2}|2[3-9][0-9]{3}|[3-6][0-9]{4}|7[01][0-9]{3}|720[0-9]{2})[0-9]{10}$/.test(
        cleaned,
      )
    ) {
      return 'MasterCard';
    } else if (/^3[47][0-9]{13}$/.test(cleaned)) {
      return 'AmericanExpress';
    }

    return 'Visa';
  }

  async pay() {
    if (this.paymentForm.invalid) {
      return;
    }

    const pan = this.paymentForm.get('creditCard')?.value!;
    this.paymentRequest.creditCardInfo = {
      pan,
      expMonth: this.paymentForm.get('expirationMonth')?.value!,
      expYear: this.paymentForm.get('expirationYear')?.value!,
      cv: this.paymentForm.get('cvv')?.value!,
      providerName: this.getCardProvider(pan),
    };

    try {
      this.swalService.showLoading('Realizando el pago');

      if (this.paymentRequest.currency == 'USD') {
        const result = await this.paymentService.getRate();

        this.paymentRequest.rate = result as any;
      }

      const result = await lastValueFrom(
        this.paymentService.doPayment(this.paymentRequest),
      );
      this.paymentService.getBalance();

      if (result) {
        await this.swalService.success(
          'El pago se ha procesado correctamente<br>Código de autorización: ' +
            result.authorizationCode +
            '<br>Número de recibo: ' +
            result.receiptNumber,
        );
        this.paymentService.clearStoredPolicies();
        this.paymentService.policyInfos = [];
        this.router.navigate(['/policies'], { replaceUrl: true });
      } else {
        this.swalService.notification('Error al procesar el pago', true);
      }
    } catch (e) {
      console.log(e);
      const message =
        typeof e === 'string' && e
          ? e
          : (e as { message?: string })?.message || 'Error al procesar el pago';
      this.swalService.notification(message, true);
    }
  }
}
